import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Tree,
  message
} from 'antd';
import { CaretDownOutlined, CaretRightOutlined, MinusOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import type { DataNode } from 'antd/es/tree';
import {
  featureNosFromDetail,
  getPlanFeatureGroupDetail,
  getPlanFeatureGroups
} from '../permissionGroups/permissionGroupManageData';
import {
  createPlan,
  getPlanDetail,
  updatePlan,
  type CreatePlanFeatureDescription,
  type CreatePlanFeaturePolicy,
  type CreatePlanPrice,
  type PlanFeatureDescription,
  type PlanSku
} from './packageManageData';
import {
  CURRENCY_OPTIONS,
  FEATURE_TYPE_OPTIONS,
  REGION_OPTIONS,
  REVISION_PERIOD_OPTIONS
} from './packageFormOptions';

const GROUP_NO_PATTERN = /^[0-9]{21}$/;
const FEATURE_NO_PATTERN = /^[0-9]{21}$/;
const POLICY_PERIOD_OPTIONS = [
  { value: 'DAY', label: '日' },
  { value: 'WEEK', label: '周' },
  { value: 'MONTH', label: '月' },
  { value: 'TOTAL', label: '总数' }
] as const;
const QUOTA_PERIOD_OPTIONS = [
  { value: 'DAY', label: '日' },
  { value: 'WEEK', label: '周' },
  { value: 'MONTH', label: '月' },
  { value: 'TOTAL', label: '总数' }
] as const;
const REGION_CODE_PATTERN = /^[A-Z]{2}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
const QUOTA_PERIODS = ['DAY', 'WEEK', 'MONTH', 'TOTAL', 'NONE'];
const REVISION_PERIODS = ['DAY', 'WEEK', 'MONTH', 'TOTAL'] as const;
const CURRENCY_FRACTION_DIGITS: Record<string, number> = {
  CNY: 2,
  USD: 2,
  THB: 2,
  IDR: 2,
  MYR: 2,
  PHP: 2,
  SGD: 2,
  VND: 0
};

function normalizeQuotaPeriod(raw?: string) {
  const text = raw?.trim() ?? '';
  if (!text) return '';
  const upper = text.toUpperCase();
  if (QUOTA_PERIODS.includes(upper)) return upper;
  const alias: Record<string, string> = {
    日: 'DAY',
    天: 'DAY',
    周: 'WEEK',
    月: 'MONTH',
    总次数: 'TOTAL',
    累计: 'TOTAL',
    持续容量: 'NONE',
    持续: 'NONE'
  };
  return alias[text] ?? '';
}

function toAmountMinor(price: number, currency: string) {
  const digits = CURRENCY_FRACTION_DIGITS[currency] ?? 2;
  const minor = Math.round(price * 10 ** digits);
  if (!Number.isSafeInteger(minor)) return null;
  return minor;
}

function fromAmountMinor(amountMinor: number, currency: string, fractionDigits?: number) {
  const digits =
    fractionDigits == null || fractionDigits < 0
      ? CURRENCY_FRACTION_DIGITS[currency] ?? 2
      : Math.min(Math.trunc(fractionDigits), 8);
  return amountMinor / 10 ** digits;
}

const PLAN_CODE_PATTERN = /^[0-9]{21}$/;
const DEFAULT_REVISION = {
  shopBaseLimit: 0,
  inviteBaseLimit: 0,
  invitePeriod: 'MONTH',
  dmBaseLimit: 0,
  dmPeriod: 'MONTH',
  mailBaseLimit: 0,
  mailPeriod: 'MONTH'
};

type FeaturePolicyDraft = {
  id: string;
  featureNo: string;
  period: string;
  quotaLimit?: number | null;
};

function createPolicyDraftId() {
  return `fp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

type PackageFormValues = {
  name: string;
  level: number;
  version: string;
  status: 'listed' | 'unlisted';
  shopBaseLimit?: number;
  inviteBaseLimit?: number;
  invitePeriod?: string;
  dmBaseLimit?: number;
  dmPeriod?: string;
  mailBaseLimit?: number;
  mailPeriod?: string;
  prices: Array<{
    price?: number;
    validityDays?: number;
    originalPrice?: number;
    region?: string;
    currency?: string;
  }>;
  features: Array<{
    displayName?: string;
    type?: 'QUOTA' | 'TEXT' | 'BOOL';
    limitValue?: number;
    unit?: string;
    period?: string;
    text?: string;
    allowed?: boolean;
    sort?: number;
  }>;
  detailQuota?: {
    visitCount?: number;
    cycleType?: string;
    freeCertMinutes?: number;
    enabled?: boolean;
  };
  exportQuotas: Array<{
    group?: string;
    totalQuota?: number;
    cycle?: string;
    singleLimit?: number;
    enabled?: boolean;
  }>;
};

function normalizeRevisionPeriod(raw?: string) {
  const upper = raw?.trim().toUpperCase() ?? '';
  return (REVISION_PERIODS as readonly string[]).includes(upper) ? upper : '';
}

function nonNegIntRule(messageText: string) {
  return {
    validator: (_: unknown, value: unknown) => {
      if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(messageText));
    }
  };
}

function mapFeatureDescriptions(list?: PlanFeatureDescription[]): PackageFormValues['features'] {
  if (!list?.length) {
    return [
      { type: 'QUOTA', limitValue: 0, sort: 1 },
      { type: 'TEXT', sort: 2 },
      { type: 'BOOL', allowed: true, sort: 3 }
    ];
  }
  return list.map((item, index) => {
    const displayType = item.displayType === 'QUOTA' || item.displayType === 'TEXT' ? item.displayType : 'BOOL';
    return {
      displayName: item.title?.trim() ?? '',
      type: displayType,
      limitValue: item.quota,
      unit: item.unit?.trim() ?? '',
      period: normalizeQuotaPeriod(item.period) || undefined,
      text: item.text?.trim() ?? '',
      allowed: item.supported !== false,
      sort: index + 1
    };
  });
}

function mapPrices(list?: PlanSku[]): PackageFormValues['prices'] {
  if (!list?.length) {
    return [];
  }
  return list.map((item) => {
    const currency = item.currency?.trim().toUpperCase() ?? '';
    const amountMinor = item.amountMinor;
    return {
      price:
        amountMinor == null || !Number.isFinite(amountMinor)
          ? 0
          : fromAmountMinor(amountMinor, currency, item.currencyFractionDigits),
      validityDays: item.durationDays,
      originalPrice: 0,
      region: item.regionCode?.trim().toUpperCase() ?? '',
      currency
    };
  });
}

type PackagePermissionNode = {
  key: string;
  displayName: string;
  loaded?: boolean;
  children?: PackagePermissionNode[];
};

function toTreeData(nodes: PackagePermissionNode[], asLeaf = false): DataNode[] {
  return nodes.map((node) => {
    if (asLeaf) {
      return { key: node.key, title: node.displayName, isLeaf: true, selectable: false, checkable: false };
    }
    const children = node.children ?? [];
    return {
      key: node.key,
      title: node.displayName,
      isLeaf: Boolean(node.loaded && children.length === 0),
      children: children.length > 0 ? toTreeData(children, true) : undefined
    };
  });
}

function filterTree(nodes: PackagePermissionNode[], keyword: string): PackagePermissionNode[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return nodes;
  return nodes
    .map((n) => {
      const children = n.children ? filterTree(n.children, keyword) : undefined;
      const selfMatch = n.displayName.toLowerCase().includes(q);
      if (selfMatch || (children && children.length > 0)) {
        return { ...n, children };
      }
      return null;
    })
    .filter(Boolean) as PackagePermissionNode[];
}


function RowActions({
  onAdd,
  onRemove,
  removeDisabled
}: {
  onAdd: () => void;
  onRemove: () => void;
  removeDisabled?: boolean;
}) {
  return (
    <Space size={8} className='admin-pkg-form-row-actions'>
      <Button type='primary' size='small' icon={<PlusOutlined />} onClick={onAdd} />
      <Button
        type='primary'
        size='small'
        icon={<MinusOutlined />}
        disabled={removeDisabled}
        onClick={onRemove}
      />
    </Space>
  );
}

function FloatField({
  label,
  className,
  children
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`admin-pkg-float-field${className ? ` ${className}` : ''}`}>
      <span className='admin-pkg-float-label'>{label}</span>
      {children}
    </div>
  );
}

function FeatureTypeFields({ name }: { name: number }) {
  const type = Form.useWatch(['features', name, 'type']) as string | undefined;

  if (type === 'QUOTA') {
    return (
      <>
        <FloatField label='限制值' className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Form.Item name={[name, 'limitValue']} noStyle>
            <InputNumber min={0} style={{ width: '100%' }} placeholder='0' />
          </Form.Item>
        </FloatField>
        <Form.Item name={[name, 'unit']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Input placeholder='单位' />
        </Form.Item>
        <Form.Item name={[name, 'period']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Select
            allowClear
            placeholder='周期'
            options={[...QUOTA_PERIOD_OPTIONS]}
          />
        </Form.Item>
      </>
    );
  }

  if (type === 'TEXT') {
    return (
      <Form.Item name={[name, 'text']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--text'>
        <Input placeholder='文本' />
      </Form.Item>
    );
  }

  return (
    <Form.Item name={[name, 'allowed']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--radio'>
      <Radio.Group>
        <Radio value={true}>允许</Radio>
        <Radio value={false}>不允许</Radio>
      </Radio.Group>
    </Form.Item>
  );
}

export function PackageForm() {
  const navigate = useNavigate();
  const { planCode: planCodeParam } = useParams();
  const planCode = PLAN_CODE_PATTERN.test(planCodeParam?.trim() ?? '') ? planCodeParam!.trim() : '';
  const isEdit = Boolean(planCode);
  const [form] = Form.useForm<PackageFormValues>();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [featurePolicies, setFeaturePolicies] = useState<FeaturePolicyDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(isEdit);
  const [groupNodes, setGroupNodes] = useState<PackagePermissionNode[]>([]);
  const [leftKeyword, setLeftKeyword] = useState('');
  const [rightKeyword, setRightKeyword] = useState('');
  const [rightExpandedKeys, setRightExpandedKeys] = useState<string[]>([]);
  const aliveRef = useRef(true);
  const loadedFeaturesRef = useRef(new Map<string, string[]>());
  const inflightRef = useRef(new Map<string, Promise<string[]>>());
  const configHashRef = useRef('');
  const revisionRef = useRef({ ...DEFAULT_REVISION });

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const collected: PackagePermissionNode[] = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getPlanFeatureGroups({ pageNum, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权限组加载失败');
          break;
        }
        records.forEach((item) => {
          const groupNo = item.groupNo?.trim() ?? '';
          if (!GROUP_NO_PATTERN.test(groupNo)) return;
          collected.push({
            key: groupNo,
            displayName: item.displayName?.trim() || item.groupCode?.trim() || groupNo
          });
        });
        if (records.length < 100) break;
      }
      if (!cancelled) setGroupNodes(collected);
    };
    load().catch(() => {
      if (!cancelled) message.error('权限组加载失败');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) {
      configHashRef.current = '';
      revisionRef.current = { ...DEFAULT_REVISION };
      setFeaturePolicies([]);
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    const loadDetail = async () => {
      const res = await getPlanDetail(planCode);
      if (cancelled) return;
      if (res.code !== 200 || !res.content?.data?.plan) {
        if (res.code !== -1) message.error(res.message || '套餐详情加载失败');
        configHashRef.current = '';
        setDetailLoading(false);
        return;
      }
      const hash = res.content.configHash;
      if (typeof hash !== 'string' || hash === '') {
        message.warning('缺少配置校验值，请关闭后重新打开');
        configHashRef.current = '';
        setDetailLoading(false);
        return;
      }
      configHashRef.current = hash;
      const plan = res.content.data.plan;
      const current = res.content.data.currentRevision;
      const revision = current?.revision;
      revisionRef.current = {
        shopBaseLimit: revision?.shopBaseLimit ?? 0,
        inviteBaseLimit: revision?.inviteBaseLimit ?? 0,
        invitePeriod: revision?.invitePeriod?.trim() || 'MONTH',
        dmBaseLimit: revision?.dmBaseLimit ?? 0,
        dmPeriod: revision?.dmPeriod?.trim() || 'MONTH',
        mailBaseLimit: revision?.mailBaseLimit ?? 0,
        mailPeriod: revision?.mailPeriod?.trim() || 'MONTH'
      };
      const groupCodes = (current?.groupCodes ?? [])
        .map((item) => item.trim())
        .filter((item) => GROUP_NO_PATTERN.test(item));
      setSelectedKeys(groupCodes);
      setFeaturePolicies(
        (current?.featurePolicies ?? [])
          .map((item) => {
            const featureNo = item.featureNo?.trim() ?? '';
            const period = item.period?.trim() ?? '';
            if (!FEATURE_NO_PATTERN.test(featureNo) || !period) return null;
            const draft: FeaturePolicyDraft = {
              id: createPolicyDraftId(),
              featureNo,
              period
            };
            if (item.quotaLimit != null && Number.isFinite(item.quotaLimit)) {
              draft.quotaLimit = Math.trunc(item.quotaLimit);
            }
            return draft;
          })
          .filter((item): item is FeaturePolicyDraft => item != null)
      );
      form.setFieldsValue({
        name: plan.displayName?.trim() ?? '',
        level: plan.grade ?? 0,
        version: revision?.revisionNo == null ? '' : String(revision.revisionNo),
        status: plan.visible === true ? 'listed' : 'unlisted',
        shopBaseLimit: revision?.shopBaseLimit ?? 0,
        inviteBaseLimit: revision?.inviteBaseLimit ?? 0,
        invitePeriod: normalizeRevisionPeriod(revision?.invitePeriod) || 'MONTH',
        dmBaseLimit: revision?.dmBaseLimit ?? 0,
        dmPeriod: normalizeRevisionPeriod(revision?.dmPeriod) || 'MONTH',
        mailBaseLimit: revision?.mailBaseLimit ?? 0,
        mailPeriod: normalizeRevisionPeriod(revision?.mailPeriod) || 'MONTH',
        features: mapFeatureDescriptions(current?.featureDescriptions),
        prices: mapPrices(current?.prices),
        detailQuota: {},
        exportQuotas: [{}]
      });
      setDetailLoading(false);
    };
    loadDetail().catch(() => {
      if (!cancelled) {
        message.error('套餐详情加载失败');
        setDetailLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [form, isEdit, planCode]);

  const loadGroupFeatures = (groupNo: string) => {
    if (!GROUP_NO_PATTERN.test(groupNo)) return Promise.resolve([]);
    const cached = loadedFeaturesRef.current.get(groupNo);
    if (cached) return Promise.resolve(cached);
    const pending = inflightRef.current.get(groupNo);
    if (pending) return pending;
    const task = (async () => {
      const res = await getPlanFeatureGroupDetail(groupNo);
      if (!aliveRef.current) return [];
      if (res.code !== 200 || !res.content?.data) {
        if (res.code !== -1) message.error(res.message || '权限组详情加载失败');
        throw new Error('detail failed');
      }
      const data = res.content.data;
      const features = Array.isArray(data.features) ? data.features : [];
      const children = features
        .map((item) => {
          const featureNo = item.featureNo?.trim() ?? '';
          if (!GROUP_NO_PATTERN.test(featureNo)) return null;
          return {
            key: featureNo,
            displayName: item.displayName?.trim() || item.featureCode?.trim() || featureNo
          };
        })
        .filter((item): item is NonNullable<typeof item> => item != null);
      const featureNos = featureNosFromDetail(data);
      if (!aliveRef.current) return featureNos;
      loadedFeaturesRef.current.set(groupNo, featureNos);
      setGroupNodes((prev) =>
        prev.map((item) =>
          item.key === groupNo
            ? {
              ...item,
              displayName: data.displayName?.trim() || item.displayName,
              loaded: true,
              children
            }
            : item
        )
      );
      return featureNos;
    })().finally(() => {
      inflightRef.current.delete(groupNo);
    });
    inflightRef.current.set(groupNo, task);
    return task;
  };

  const leftTree = useMemo(() => filterTree(groupNodes, leftKeyword), [groupNodes, leftKeyword]);
  const visibleGroupKeys = useMemo(() => leftTree.map((node) => node.key), [leftTree]);

  const featuresOfGroup = (groupNo: string) => {
    const cached = loadedFeaturesRef.current.get(groupNo);
    if (cached) return cached;
    const node = groupNodes.find((item) => item.key === groupNo);
    return (node?.children ?? []).map((child) => child.key);
  };

  const rightItems = useMemo(() => {
    const q = rightKeyword.trim().toLowerCase();
    return selectedKeys
      .map((key) => {
        const node = groupNodes.find((item) => item.key === key);
        const displayName = node?.displayName || key;
        const features = (node?.children ?? [])
          .map((child) => ({
            featureNo: child.key,
            displayName: child.displayName
          }))
          .filter(
            (feature) =>
              !q ||
              feature.displayName.toLowerCase().includes(q) ||
              displayName.toLowerCase().includes(q)
          );
        if (q && !displayName.toLowerCase().includes(q) && features.length === 0) return null;
        return {
          key,
          displayName,
          features,
          loading: !loadedFeaturesRef.current.has(key) && !node?.loaded
        };
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [groupNodes, rightKeyword, selectedKeys]);

  const policiesByFeature = useMemo(() => {
    const map = new Map<string, FeaturePolicyDraft[]>();
    featurePolicies.forEach((policy) => {
      const list = map.get(policy.featureNo) ?? [];
      list.push(policy);
      map.set(policy.featureNo, list);
    });
    return map;
  }, [featurePolicies]);

  useEffect(() => {
    selectedKeys.forEach((groupNo) => {
      void loadGroupFeatures(groupNo).catch(() => undefined);
    });
    setRightExpandedKeys((prev) => {
      const selected = new Set(selectedKeys);
      const kept = prev.filter((key) => selected.has(key));
      const next = [...kept];
      selectedKeys.forEach((key) => {
        if (!next.includes(key)) next.push(key);
      });
      return next.length === prev.length && next.every((key, index) => key === prev[index]) ? prev : next;
    });
  }, [selectedKeys]);

  useEffect(() => {
    if (selectedKeys.length === 0) {
      setFeaturePolicies((prev) => (prev.length ? [] : prev));
      return;
    }
    const allLoaded = selectedKeys.every(
      (key) => loadedFeaturesRef.current.has(key) || Boolean(groupNodes.find((node) => node.key === key)?.loaded)
    );
    if (!allLoaded) return;
    const allowed = new Set<string>();
    selectedKeys.forEach((groupNo) => {
      featuresOfGroup(groupNo).forEach((featureNo) => allowed.add(featureNo));
    });
    setFeaturePolicies((prev) => {
      const next = prev.filter((policy) => allowed.has(policy.featureNo));
      return next.length === prev.length ? prev : next;
    });
  }, [selectedKeys, groupNodes]);

  const removePoliciesForGroups = (groupNos: string[]) => {
    if (groupNos.length === 0) return;
    const removedFeatures = new Set<string>();
    groupNos.forEach((groupNo) => {
      featuresOfGroup(groupNo).forEach((featureNo) => removedFeatures.add(featureNo));
    });
    if (removedFeatures.size === 0) return;
    const remainingGroups = selectedKeys.filter((key) => !groupNos.includes(key));
    const keptFeatures = new Set<string>();
    remainingGroups.forEach((groupNo) => {
      featuresOfGroup(groupNo).forEach((featureNo) => keptFeatures.add(featureNo));
    });
    setFeaturePolicies((prev) =>
      prev.filter((policy) => !removedFeatures.has(policy.featureNo) || keptFeatures.has(policy.featureNo))
    );
  };

  const toggleLeaf = (key: string, checked: boolean) => {
    if (!checked) removePoliciesForGroups([key]);
    setSelectedKeys((prev) => {
      if (checked) {
        return prev.includes(key) ? prev : [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
  };

  const onCheckTree = (
    checked:
      | React.Key[]
      | {
        checked: React.Key[];
        halfChecked: React.Key[];
      }
  ) => {
    const keys = (Array.isArray(checked) ? checked : checked.checked).map(String);
    const groupKeySet = new Set(groupNodes.map((node) => node.key));
    const checkedGroups = keys.filter((key) => groupKeySet.has(key) && visibleGroupKeys.includes(key));
    const hiddenSelected = selectedKeys.filter((key) => !visibleGroupKeys.includes(key));
    const next = Array.from(new Set([...hiddenSelected, ...checkedGroups]));
    const removed = selectedKeys.filter((key) => visibleGroupKeys.includes(key) && !next.includes(key));
    removePoliciesForGroups(removed);
    setSelectedKeys(next);
  };

  const selectAllVisible = (checked: boolean) => {
    if (!checked) {
      removePoliciesForGroups(visibleGroupKeys.filter((key) => selectedKeys.includes(key)));
    }
    setSelectedKeys((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...visibleGroupKeys]));
      }
      return prev.filter((key) => !visibleGroupKeys.includes(key));
    });
  };

  const addFeaturePolicy = (featureNo: string) => {
    if (!FEATURE_NO_PATTERN.test(featureNo)) return;
    setFeaturePolicies((prev) => [
      ...prev,
      { id: createPolicyDraftId(), featureNo, period: '', quotaLimit: null }
    ]);
  };

  const updateFeaturePolicy = (id: string, patch: Partial<Pick<FeaturePolicyDraft, 'period' | 'quotaLimit'>>) => {
    setFeaturePolicies((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const removeFeaturePolicy = (id: string) => {
    setFeaturePolicies((prev) => prev.filter((item) => item.id !== id));
  };

  const onFinish = async (values: PackageFormValues) => {
    if (saving || detailLoading) return;
    const displayName = values.name?.trim() ?? '';
    if (!displayName || displayName.length > 128) {
      message.error('套餐名称必填，最多 128 个字符');
      return;
    }
    if (!isEdit && (!Number.isInteger(values.level) || values.level < 0)) {
      message.error('套餐等级须为非负整数');
      return;
    }
    if (isEdit) {
      const hash = configHashRef.current;
      if (typeof hash !== 'string' || hash === '') {
        message.warning('缺少配置校验值，请关闭后重新打开');
        return;
      }
    }
    const groupCodes = selectedKeys.filter((key) => GROUP_NO_PATTERN.test(key));
    if (groupCodes.length === 0) {
      message.error('请选择套餐权限');
      return;
    }

    const orderedFeatures = (values.features ?? [])
      .map((item, index) => ({ item, index }))
      .sort((a, b) => (a.item.sort ?? a.index + 1) - (b.item.sort ?? b.index + 1) || a.index - b.index);
    const featureDescriptions: CreatePlanFeatureDescription[] = [];
    for (const { item } of orderedFeatures) {
      const title = item.displayName?.trim() ?? '';
      const displayType = item.type ?? 'BOOL';
      if (!title) {
        message.error('请输入功能显示名称');
        return;
      }
      if (displayType === 'QUOTA') {
        const period = normalizeQuotaPeriod(item.period);
        const unit = item.unit?.trim() ?? '';
        const quota = item.limitValue ?? 0;
        if (!unit || !Number.isInteger(quota) || quota < 0 || !QUOTA_PERIODS.includes(period)) {
          message.error('数量额度需填写非负限制值、单位，以及 DAY、WEEK、MONTH、TOTAL 或 NONE');
          return;
        }
        featureDescriptions.push({ title, displayType, quota, unit, period });
        continue;
      }
      if (displayType === 'TEXT') {
        const text = item.text?.trim() ?? '';
        if (!text) {
          message.error('请输入文本说明');
          return;
        }
        featureDescriptions.push({ title, displayType, text });
        continue;
      }
      featureDescriptions.push({ title, displayType: 'BOOL', supported: item.allowed !== false });
    }

    const prices: CreatePlanPrice[] = [];
    for (const item of values.prices ?? []) {
      const regionCode = item.region?.trim().toUpperCase() ?? '';
      const currency = item.currency?.trim().toUpperCase() ?? '';
      const durationDays = item.validityDays;
      if (!REGION_CODE_PATTERN.test(regionCode) || !CURRENCY_CODE_PATTERN.test(currency)) {
        message.error('请选择国家/地区和货币');
        return;
      }
      if (!Number.isInteger(durationDays) || durationDays == null || durationDays < 1) {
        message.error('有效期须为不少于 1 的整数天');
        return;
      }
      const price: CreatePlanPrice = { regionCode, currency, durationDays };
      if (item.price != null && item.price !== 0) {
        const amountMinor = toAmountMinor(item.price, currency);
        if (amountMinor == null || amountMinor < 1) {
          message.error('价格须为大于 0 的金额');
          return;
        }
        price.amountMinor = amountMinor;
      }
      prices.push(price);
    }
    if (prices.length > 100) {
      message.error('售卖规格最多 100 条');
      return;
    }

    const shopBaseLimit = values.shopBaseLimit;
    const inviteBaseLimit = values.inviteBaseLimit;
    const dmBaseLimit = values.dmBaseLimit;
    const mailBaseLimit = values.mailBaseLimit;
    const invitePeriod = normalizeRevisionPeriod(values.invitePeriod);
    const dmPeriod = normalizeRevisionPeriod(values.dmPeriod);
    const mailPeriod = normalizeRevisionPeriod(values.mailPeriod);
    if (
      !Number.isInteger(shopBaseLimit) ||
      shopBaseLimit == null ||
      shopBaseLimit < 0 ||
      !Number.isInteger(inviteBaseLimit) ||
      inviteBaseLimit == null ||
      inviteBaseLimit < 0 ||
      !Number.isInteger(dmBaseLimit) ||
      dmBaseLimit == null ||
      dmBaseLimit < 0 ||
      !Number.isInteger(mailBaseLimit) ||
      mailBaseLimit == null ||
      mailBaseLimit < 0
    ) {
      message.error('套餐策略额度须为非负整数');
      return;
    }
    if (!invitePeriod || !dmPeriod || !mailPeriod) {
      message.error('请选择邀约/私信/邮件额度周期');
      return;
    }

    const allowedFeatureNos = new Set<string>();
    groupCodes.forEach((groupNo) => {
      featuresOfGroup(groupNo).forEach((featureNo) => allowedFeatureNos.add(featureNo));
    });
    const nextFeaturePolicies: CreatePlanFeaturePolicy[] = [];
    for (const draft of featurePolicies) {
      const featureNo = draft.featureNo?.trim() ?? '';
      const period = draft.period?.trim().toUpperCase() ?? '';
      if (!FEATURE_NO_PATTERN.test(featureNo)) {
        message.error('功能编码须为 21 位数字');
        return;
      }
      if (!allowedFeatureNos.has(featureNo)) {
        message.error('策略只能引用已选权限组内的功能点');
        return;
      }
      if (!(REVISION_PERIODS as readonly string[]).includes(period)) {
        message.error('请选择额度周期');
        return;
      }
      const policy: CreatePlanFeaturePolicy = { featureNo, period };
      if (draft.quotaLimit != null) {
        if (!Number.isInteger(draft.quotaLimit) || draft.quotaLimit < 0) {
          message.error('额度上限须为非负整数');
          return;
        }
        policy.quotaLimit = draft.quotaLimit;
      }
      nextFeaturePolicies.push(policy);
    }

    const revision = {
      shopBaseLimit,
      inviteBaseLimit,
      invitePeriod,
      dmBaseLimit,
      dmPeriod,
      mailBaseLimit,
      mailPeriod,
      groupCodes,
      featurePolicies: nextFeaturePolicies
    };

    setSaving(true);
    const res = isEdit
      ? await updatePlan(planCode, {
        displayName,
        featureDescriptions,
        revision,
        prices,
        expectedConfigHash: configHashRef.current
      })
      : await createPlan({
        displayName,
        grade: values.level,
        featureDescriptions,
        revision,
        prices
      });
    setSaving(false);
    if (res.code !== 200) {
      if (res.code !== -1) {
        message.error(
          res.message || (isEdit ? (res.code === 409 ? '配置已变化，请关闭后重新打开' : '套餐保存失败') : '套餐创建失败')
        );
      }
      return;
    }
    message.success(res.message || (isEdit ? '套餐已保存' : '套餐已创建'));
    navigate('/devops/creator-outreach/packages');
  };

  return (
    <div className='admin-page-card admin-pkg-form-page'>
      <div className='admin-pkg-form-header'>
        <h2 className='admin-pkg-form-title'>{isEdit ? '编辑套餐' : '新增套餐'}</h2>
        <Space>
          <Button onClick={() => navigate('/devops/creator-outreach/packages')}>取消</Button>
          <Button
            type='primary'
            className='admin-pkg-create-btn'
            loading={saving}
            disabled={detailLoading}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        </Space>
      </div>

      {detailLoading ? <Spin className='block py-16' /> : null}

      <Form<PackageFormValues>
        form={form}
        layout='horizontal'
        labelCol={{ flex: '130px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        requiredMark
        className='admin-pkg-form'
        style={detailLoading ? { display: 'none' } : undefined}
        onFinish={onFinish}
        initialValues={{
          level: 1,
          status: 'unlisted',
          shopBaseLimit: 0,
          inviteBaseLimit: 0,
          invitePeriod: 'MONTH',
          dmBaseLimit: 0,
          dmPeriod: 'MONTH',
          mailBaseLimit: 0,
          mailPeriod: 'MONTH',
          prices: [],
          features: [
            { type: 'QUOTA', limitValue: 0, sort: 1 },
            { type: 'TEXT', sort: 2 },
            { type: 'BOOL', allowed: true, sort: 3 }
          ],
          detailQuota: {},
          exportQuotas: [{}]
        }}
      >
        <div className='admin-pkg-form-grid'>
          <Form.Item
            name='name'
            label='套餐名称'
            rules={[{ required: true, message: '请输入套餐名称' }]}
          >
            <Input placeholder='请输入套餐名称' />
          </Form.Item>
          <Form.Item
            name='level'
            label='套餐等级'
            rules={[{ required: true, message: '请输入套餐等级' }]}
          >
            <InputNumber className='w-full' min={0} precision={0} style={{ width: '100%' }} disabled={isEdit} />
          </Form.Item>
          <Form.Item
            name='version'
            label='套餐版本'
            rules={[{ required: true, message: '请输入套餐版本号' }]}
          >
            <Input placeholder='请输入套餐版本号' disabled={isEdit} />
          </Form.Item>
          <Form.Item
            name='status'
            label='套餐状态'
            rules={[{ required: true, message: '请选择套餐状态' }]}
          >
            <Radio.Group>
              <Radio value='listed'>上架</Radio>
              <Radio value='unlisted'>下架</Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.List name='prices'>
          {(fields, { add, remove }) => (
            <Form.Item label='套餐价格' className='admin-pkg-form-block'>
              <div className='admin-pkg-form-list'>
                {fields.length === 0 ? (
                  <div className='admin-benefit-feature-empty-row mt-1'>
                    <Button
                      type='link'
                      size='small'
                      icon={<PlusOutlined />}
                      onClick={() => add({ price: 0, validityDays: 30, originalPrice: 0 })}
                    >
                      添加价格
                    </Button>
                  </div>
                ) : (
                  fields.map((field) => (
                    <div key={field.key} className='admin-pkg-form-inline-row'>
                      <Form.Item name={[field.name, 'price']} label='价格' className='admin-pkg-form-mini'>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'validityDays']}
                        label='有效期'
                        className='admin-pkg-form-mini'
                      >
                        <InputNumber min={1} addonAfter='天' style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'originalPrice']}
                        label='原价'
                        className='admin-pkg-form-mini'
                      >
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'region']}
                        label='国家/地区'
                        className='admin-pkg-form-mini'
                        rules={[{ required: true, message: '请选择' }]}
                      >
                        <Select options={REGION_OPTIONS} placeholder='请选择' allowClear />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'currency']}
                        label='货币代码'
                        className='admin-pkg-form-mini'
                        rules={[{ required: true, message: '请选择' }]}
                      >
                        <Select options={CURRENCY_OPTIONS} placeholder='请选择' allowClear />
                      </Form.Item>
                      <RowActions
                        onAdd={() => add({ price: 0, validityDays: 30, originalPrice: 0 })}
                        onRemove={() => remove(field.name)}
                      />
                    </div>
                  ))
                )}
              </div>
            </Form.Item>
          )}
        </Form.List>

        <Form.List name='features'>
          {(fields, { add, remove }) => (
            <Form.Item
              required
              label='套餐功能描述'
              className='admin-pkg-form-block'
            >
              <div className='admin-pkg-form-list'>
                {fields.map((field) => (
                  <div key={field.key} className='admin-pkg-form-feature-row'>
                    <Form.Item
                      name={[field.name, 'displayName']}
                      className='admin-pkg-form-ctrl admin-pkg-form-ctrl--name'
                      rules={[{ required: true, message: '请输入显示名称' }]}
                    >
                      <Input placeholder='左侧显示名称' />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'type']}
                      className='admin-pkg-form-ctrl admin-pkg-form-ctrl--type'
                    >
                      <Select options={FEATURE_TYPE_OPTIONS} />
                    </Form.Item>
                    <FeatureTypeFields name={field.name} />
                    <FloatField label='显示排序' className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sort'>
                      <Form.Item name={[field.name, 'sort']} noStyle>
                        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </FloatField>
                    <RowActions
                      onAdd={() =>
                        add({ type: 'BOOL', allowed: true, sort: fields.length + 1 })
                      }
                      onRemove={() => remove(field.name)}
                      removeDisabled={fields.length <= 1}
                    />
                  </div>
                ))}
              </div>
            </Form.Item>
          )}
        </Form.List>

        <Form.Item required label='套餐权限' className='admin-pkg-form-block'>
          <div className='admin-pkg-transfer'>
            <div className='admin-pkg-transfer-pane'>
              <div className='admin-pkg-transfer-head'>
                <Checkbox
                  checked={visibleGroupKeys.length > 0 && visibleGroupKeys.every((key) => selectedKeys.includes(key))}
                  indeterminate={
                    visibleGroupKeys.some((key) => selectedKeys.includes(key)) &&
                    !visibleGroupKeys.every((key) => selectedKeys.includes(key))
                  }
                  onChange={(e) => selectAllVisible(e.target.checked)}
                >
                  {groupNodes.length} 可选分组
                </Checkbox>
              </div>
              <Input
                allowClear
                prefix={<SearchOutlined className='text-slate-400' />}
                placeholder='请输入搜索内容'
                value={leftKeyword}
                onChange={(e) => setLeftKeyword(e.target.value)}
                className='admin-pkg-transfer-search'
              />
              <div className='admin-pkg-transfer-body'>
                <Tree
                  checkable
                  checkStrictly
                  treeData={toTreeData(leftTree)}
                  checkedKeys={{ checked: selectedKeys, halfChecked: [] }}
                  onCheck={onCheckTree}
                  loadData={(node) => loadGroupFeatures(String(node.key)).then(() => undefined)}
                />
              </div>
            </div>
            <div className='admin-pkg-transfer-pane'>
              <div className='admin-pkg-transfer-head'>
                <span>{selectedKeys.length} 已选分组</span>
              </div>
              <Input
                allowClear
                prefix={<SearchOutlined className='text-slate-400' />}
                placeholder='请输入搜索内容'
                value={rightKeyword}
                onChange={(e) => setRightKeyword(e.target.value)}
                className='admin-pkg-transfer-search'
              />
              <div className='admin-pkg-transfer-body'>
                {rightItems.length === 0 ? (
                  <div className='admin-pkg-transfer-empty'>点击左侧分组即可添加</div>
                ) : (
                  <ul className='admin-pkg-transfer-selected'>
                    {rightItems.map((item) => {
                      const expanded = rightExpandedKeys.includes(item.key);
                      return (
                        <li key={item.key} className='admin-pkg-transfer-group'>
                          <div className='admin-pkg-transfer-group-head'>
                            <button
                              type='button'
                              className='admin-pkg-transfer-expand'
                              aria-label={expanded ? '收起' : '展开'}
                              onClick={() =>
                                setRightExpandedKeys((prev) =>
                                  expanded ? prev.filter((key) => key !== item.key) : [...prev, item.key]
                                )
                              }
                            >
                              {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                            </button>
                            <Checkbox checked onChange={() => toggleLeaf(item.key, false)}>
                              {item.displayName}
                            </Checkbox>
                          </div>
                          {expanded ? (
                            <>
                              {item.loading && item.features.length === 0 ? (
                                <div className='admin-pkg-transfer-feature-loading'>
                                  <Spin size='small' />
                                </div>
                              ) : null}
                              {item.features.length > 0 ? (
                                <ul className='admin-pkg-transfer-features'>
                                  {item.features.map((feature) => {
                                    const policies = policiesByFeature.get(feature.featureNo) ?? [];
                                    return (
                                      <li key={feature.featureNo} className='admin-pkg-transfer-feature'>
                                        <div className='admin-pkg-transfer-feature-head'>
                                          <span className='admin-pkg-transfer-feature-title'>{feature.displayName}</span>
                                          <button
                                            type='button'
                                            className='admin-link-btn text-xs'
                                            onClick={() => addFeaturePolicy(feature.featureNo)}
                                          >
                                            新增策略
                                          </button>
                                        </div>
                                        {policies.map((policy) => (
                                          <div key={policy.id} className='admin-pkg-transfer-policy'>

                                            <Select
                                              allowClear
                                              placeholder='额度周期'
                                              options={[...POLICY_PERIOD_OPTIONS]}
                                              value={policy.period || undefined}
                                              onChange={(value) =>
                                                updateFeaturePolicy(policy.id, {
                                                  period: typeof value === 'string' ? value : ''
                                                })
                                              }
                                              style={{ width: '100%' }}
                                            />
                                            <InputNumber
                                              min={0}
                                              precision={0}
                                              placeholder='额度上限'
                                              value={policy.quotaLimit == null ? undefined : policy.quotaLimit}
                                              onChange={(value) =>
                                                updateFeaturePolicy(policy.id, {
                                                  quotaLimit: typeof value === 'number' ? value : null
                                                })
                                              }
                                              style={{ width: '100%' }}
                                            />
                                            <button
                                              type='button'
                                              className='admin-link-btn admin-link-btn--danger'
                                              onClick={() => removeFeaturePolicy(policy.id)}
                                            >
                                              删除
                                            </button>
                                          </div>
                                        ))}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : !item.loading ? (
                                <div className='admin-pkg-transfer-feature-empty'>该分组暂无功能点</div>
                              ) : null}
                            </>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Form.Item>

        <Form.Item required label='套餐策略' className='admin-pkg-form-block'>
          <div className='admin-pkg-strategy-grid'>
            <section className='admin-pkg-strategy-card'>
              <header className='admin-pkg-strategy-card-head'>
                <h4 className='admin-pkg-strategy-card-title'>店铺席位</h4>
                <p className='admin-pkg-strategy-card-desc'>基础可授权店铺数量，须为非负整数</p>
              </header>
              <div className='admin-pkg-strategy-card-fields admin-pkg-strategy-card-fields--single'>
                <FloatField label='基础店铺数' className='admin-pkg-strategy-field'>
                  <Form.Item
                    name='shopBaseLimit'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请输入基础店铺数' }, nonNegIntRule('须为非负整数')]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter='店' />
                  </Form.Item>
                </FloatField>
              </div>
            </section>

            <section className='admin-pkg-strategy-card'>
              <header className='admin-pkg-strategy-card-head'>
                <h4 className='admin-pkg-strategy-card-title'>定向邀约</h4>
                <p className='admin-pkg-strategy-card-desc'>接口调用次数；批量达人数不扣额，0 表示不可调用</p>
              </header>
              <div className='admin-pkg-strategy-card-fields'>
                <FloatField label='额度' className='admin-pkg-strategy-field'>
                  <Form.Item
                    name='inviteBaseLimit'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请输入邀约额度' }, nonNegIntRule('须为非负整数')]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter='次' />
                  </Form.Item>
                </FloatField>
                <FloatField label='周期' className='admin-pkg-strategy-field admin-pkg-strategy-field--period'>
                  <Form.Item
                    name='invitePeriod'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请选择邀约周期' }]}
                  >
                    <Select options={REVISION_PERIOD_OPTIONS} placeholder='请选择' />
                  </Form.Item>
                </FloatField>
              </div>
            </section>

            <section className='admin-pkg-strategy-card'>
              <header className='admin-pkg-strategy-card-head'>
                <h4 className='admin-pkg-strategy-card-title'>私信发送</h4>
                <p className='admin-pkg-strategy-card-desc'>可发送私信条数，0 表示不可发送</p>
              </header>
              <div className='admin-pkg-strategy-card-fields'>
                <FloatField label='额度' className='admin-pkg-strategy-field'>
                  <Form.Item
                    name='dmBaseLimit'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请输入私信额度' }, nonNegIntRule('须为非负整数')]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter='条' />
                  </Form.Item>
                </FloatField>
                <FloatField label='周期' className='admin-pkg-strategy-field admin-pkg-strategy-field--period'>
                  <Form.Item
                    name='dmPeriod'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请选择私信周期' }]}
                  >
                    <Select options={REVISION_PERIOD_OPTIONS} placeholder='请选择' />
                  </Form.Item>
                </FloatField>
              </div>
            </section>

            <section className='admin-pkg-strategy-card'>
              <header className='admin-pkg-strategy-card-head'>
                <h4 className='admin-pkg-strategy-card-title'>平台邮件</h4>
                <p className='admin-pkg-strategy-card-desc'>基础邮件额度，周期按北京时间（周一开始）</p>
              </header>
              <div className='admin-pkg-strategy-card-fields'>
                <FloatField label='额度' className='admin-pkg-strategy-field'>
                  <Form.Item
                    name='mailBaseLimit'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请输入邮件额度' }, nonNegIntRule('须为非负整数')]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter='封' />
                  </Form.Item>
                </FloatField>
                <FloatField label='周期' className='admin-pkg-strategy-field admin-pkg-strategy-field--period'>
                  <Form.Item
                    name='mailPeriod'
                    className='admin-pkg-strategy-control'
                    rules={[{ required: true, message: '请选择邮件周期' }]}
                  >
                    <Select options={REVISION_PERIOD_OPTIONS} placeholder='请选择' />
                  </Form.Item>
                </FloatField>
              </div>
            </section>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
